export const CalculatingT12 = () => {
  return (
    <section className="py-20 bg-gradient-to-br from-slate-50 to-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <div className="inline-block px-4 py-2 bg-teal-50 border border-teal-200 rounded-full mb-6">
            <span className="text-teal-600 text-sm font-semibold">Step-by-Step Process</span>
          </div>
          <h2 className="text-4xl font-bold text-gray-900 mb-6">
            How to Calculate Your T12
          </h2>
          <p className="text-xl text-gray-700 max-w-3xl mx-auto">
            Follow this systematic approach to compile accurate Trailing 12 financial statements that buyers and lenders will trust.
          </p>
        </div>
        
        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <div className="aspect-w-4 aspect-h-3 rounded-2xl overflow-hidden shadow-2xl">
              <img 
                src="https://public.readdy.ai/ai/img_res/232bada886d92068e7c9c151b6d30682.jpg"
                alt="Calculating trailing 12 financial statements for apartment building"
                className="w-full h-full object-cover"
              />
            </div>
          </div>
          
          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold text-xl">1</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Determine Your 12-Month Period</h3>
                  <p className="text-gray-700 mb-3">
                    Identify the most recent complete 12-month period. If today is June 15, 2024, your T12 would typically cover June 1, 2023 through May 31, 2024.
                  </p>
                  <div className="bg-teal-50 rounded-lg p-3">
                    <p className="text-sm text-gray-700"><strong>Pro Tip:</strong> Use complete months only. Don't include partial months as they skew the data.</p>
                  </div>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold text-xl">2</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Gather Monthly Income Data</h3>
                  <p className="text-gray-700 mb-3">
                    Collect all income sources for each of the 12 months:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span>Rental income (actual collected rent, not scheduled)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span>Parking fees, storage fees, pet rent</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span>Laundry income, vending machines</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span>Late fees, application fees</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold text-xl">3</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Compile Operating Expenses</h3>
                  <p className="text-gray-700 mb-3">
                    Document all operating expenses paid during the 12-month period:
                  </p>
                  <ul className="space-y-2 text-gray-700">
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span>Property taxes and insurance</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span>Utilities (water, sewer, trash, common area electric)</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span>Maintenance and repairs</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span>Property management fees</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <i className="ri-checkbox-circle-fill text-teal-600 mt-1"></i>
                      <span>Landscaping, snow removal, pest control</span>
                    </li>
                  </ul>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold text-xl">4</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Calculate Monthly Totals</h3>
                  <p className="text-gray-700">
                    For each month, calculate total income and total expenses. This gives you 12 monthly snapshots of property performance and helps identify seasonal trends.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white rounded-xl p-6 shadow-lg border border-gray-200">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-teal-100 rounded-lg flex items-center justify-center flex-shrink-0">
                  <span className="text-teal-600 font-bold text-xl">5</span>
                </div>
                <div>
                  <h3 className="text-xl font-bold text-gray-900 mb-2">Sum the 12 Months</h3>
                  <p className="text-gray-700 mb-3">
                    Add up all 12 months to get your T12 totals:
                  </p>
                  <div className="bg-slate-50 rounded-lg p-4 space-y-2">
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">T12 Gross Income:</span>
                      <span className="font-bold text-gray-900">Sum of 12 months income</span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-gray-700">T12 Operating Expenses:</span>
                      <span className="font-bold text-gray-900">Sum of 12 months expenses</span>
                    </div>
                    <div className="border-t-2 border-gray-300 pt-2 flex justify-between items-center">
                      <span className="text-gray-900 font-bold">T12 NOI:</span>
                      <span className="font-bold text-teal-600">Income - Expenses</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-2xl shadow-xl p-8 border border-gray-200">
          <h3 className="text-2xl font-bold text-gray-900 mb-6 text-center">T12 Calculation Example</h3>
          
          <div className="grid lg:grid-cols-2 gap-8">
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4">Sample 24-Unit Apartment Building</h4>
              
              <div className="space-y-4">
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="text-sm text-gray-600 mb-2">Monthly Rental Income</div>
                  <div className="space-y-1 text-sm text-gray-700">
                    <div className="flex justify-between">
                      <span>24 units × $1,200/month</span>
                      <span className="font-semibold">$28,800</span>
                    </div>
                    <div className="flex justify-between">
                      <span>× 12 months</span>
                      <span className="font-semibold">$345,600</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="text-sm text-gray-600 mb-2">Other Income (Annual)</div>
                  <div className="space-y-1 text-sm text-gray-700">
                    <div className="flex justify-between">
                      <span>Parking fees</span>
                      <span>$7,200</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Laundry income</span>
                      <span>$4,800</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Pet fees</span>
                      <span>$3,600</span>
                    </div>
                    <div className="flex justify-between border-t border-gray-300 pt-1 mt-1">
                      <span className="font-semibold">Total Other Income</span>
                      <span className="font-semibold">$15,600</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-gradient-to-br from-slate-50 to-gray-50 rounded-lg p-4 border border-gray-200">
                  <div className="text-sm text-gray-600 mb-2">Vacancy Loss</div>
                  <div className="space-y-1 text-sm text-gray-700">
                    <div className="flex justify-between">
                      <span>5% average vacancy</span>
                      <span className="text-red-600">-$17,280</span>
                    </div>
                  </div>
                </div>
                
                <div className="bg-teal-50 rounded-lg p-4 border-2 border-teal-200">
                  <div className="flex justify-between items-center">
                    <span className="font-bold text-gray-900">T12 Gross Income</span>
                    <span className="text-2xl font-bold text-teal-600">$343,920</span>
                  </div>
                </div>
              </div>
            </div>
            
            <div>
              <h4 className="text-lg font-bold text-gray-900 mb-4">Operating Expenses (Annual)</h4>
              
              <div className="space-y-3">
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Property Taxes</span>
                  <span className="font-semibold text-gray-900">$42,000</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Insurance</span>
                  <span className="font-semibold text-gray-900">$18,000</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Water & Sewer</span>
                  <span className="font-semibold text-gray-900">$14,400</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Trash Collection</span>
                  <span className="font-semibold text-gray-900">$6,000</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Common Area Electric</span>
                  <span className="font-semibold text-gray-900">$8,400</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Maintenance & Repairs</span>
                  <span className="font-semibold text-gray-900">$24,000</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Property Management (8%)</span>
                  <span className="font-semibold text-gray-900">$27,514</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Landscaping</span>
                  <span className="font-semibold text-gray-900">$7,200</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Snow Removal</span>
                  <span className="font-semibold text-gray-900">$4,800</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Pest Control</span>
                  <span className="font-semibold text-gray-900">$2,400</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Legal & Professional</span>
                  <span className="font-semibold text-gray-900">$3,600</span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-gray-700">Advertising & Marketing</span>
                  <span className="font-semibold text-gray-900">$2,400</span>
                </div>
                <div className="flex justify-between items-center text-sm border-t-2 border-gray-300 pt-3 mt-3">
                  <span className="font-bold text-gray-900">Total Operating Expenses</span>
                  <span className="text-xl font-bold text-gray-900">$160,714</span>
                </div>
              </div>
              
              <div className="mt-6 bg-gradient-to-br from-teal-500 to-teal-600 rounded-lg p-6 text-white">
                <div className="text-sm mb-2 opacity-90">Net Operating Income (NOI)</div>
                <div className="text-sm mb-1">$343,920 - $160,714</div>
                <div className="text-3xl font-bold">$183,206</div>
                <div className="mt-4 pt-4 border-t border-white/20 text-sm">
                  <div className="flex justify-between">
                    <span>At 6.5% Cap Rate:</span>
                    <span className="font-bold">$2,818,554 Value</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        
        <div className="mt-12 bg-yellow-50 border-l-4 border-yellow-500 rounded-r-lg p-6">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 bg-yellow-100 rounded-lg flex items-center justify-center flex-shrink-0">
              <i className="ri-alert-line text-2xl text-yellow-600"></i>
            </div>
            <div>
              <h4 className="font-bold text-gray-900 mb-2">Important: What NOT to Include in T12</h4>
              <ul className="space-y-2 text-gray-700">
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <span><strong>Debt Service:</strong> Mortgage payments are NOT operating expenses</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <span><strong>Capital Expenditures:</strong> Major improvements like new roofs or HVAC systems</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <span><strong>Depreciation:</strong> This is a tax concept, not an actual cash expense</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-close-circle-fill text-red-600 mt-1"></i>
                  <span><strong>Owner's Personal Expenses:</strong> Only property-related expenses count</span>
                </li>
              </ul>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};